import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, RedirectCommand, Router } from '@angular/router';

import { map, type Observable, of, take } from 'rxjs';

import { AskTemplatesDataService } from '@core/ask-hr-templates-logic';
import { I18nService } from '@core/i18n-legacy';
import {
  DialogService,
  type InferDialogData,
  ProtectedControlsService,
} from '@core/user-interface-legacy';

import { CreateTicketDialogComponent } from './create-ticket-dialog.component';

@Injectable({ providedIn: 'root' })
export class CreateTicketDialogService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly protectedControls = inject(ProtectedControlsService);
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly askTemplatesDataService = inject(AskTemplatesDataService);

  public navigateToCreateTicket(templateId: number) {
    const urlTree = this.router.createUrlTree(
      [
        {
          outlets: {
            dialog: ['ask-hr', 'create-ticket', templateId],
          },
        },
      ],
      { relativeTo: this.route },
    );
    this.router.navigateByUrl(urlTree);
  }

  public canDeactivateGuard() {
    if (this.protectedControls.locked) {
      return this.dialogService.error({
        title: this.i18n.translate('tlc.ask_hr.requests.create.discard_title'),
        message: this.i18n.translate('tlc.ask_hr.requests.create.discard_message'),
        agreeLabel: this.i18n.translate('tlc.common.confirm'),
      });
    }
    return of(true);
  }

  public resolveDialogData(route: ActivatedRouteSnapshot) {
    return this.askTemplatesDataService.loadTemplateById(+route.paramMap.get('templateId')!).pipe(
      take(1),
      map(({ isSuccess, success }) => {
        if (!isSuccess)
          return new RedirectCommand(this.router.createUrlTree(['ask-hr', 'my', 'forms']));

        return { template: success! };
      }),
    ) satisfies Observable<InferDialogData<CreateTicketDialogComponent> | RedirectCommand>;
  }
}
